import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BusinessIcon from "@mui/icons-material/Business";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SchoolIcon from "@mui/icons-material/School";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GroupsIcon from "@mui/icons-material/Groups";
import ScienceIcon from "@mui/icons-material/Science";
import PublicIcon from "@mui/icons-material/Public";

const ORGANIZATIONS = [
  {
    id: 1,
    name: "Amyloidosis Research Consortium",
    acronym: "ARC",
    emoji: "🔬",
    description:
      "A collaborative research organization dedicated to developing effective treatments for amyloidosis through innovative clinical research and patient support programs.",
    website: "https://arci.org",
    category: "Research",
    color: "#1A5EA8",
    tags: ["Research", "Clinical Trials", "Patient Registry"],
    services: [
      "Clinical trial coordination",
      "Research funding",
      "Patient registry",
    ],
  },
  {
    id: 2,
    name: "Amyloidosis Support Groups",
    acronym: "ASG",
    emoji: "🤝",
    description:
      "The largest network of amyloidosis patient support groups, connecting patients, caregivers, and families worldwide across all types of amyloidosis.",
    website: "https://amyloidosissupport.org",
    category: "Support",
    color: "#f093fb",
    tags: ["Support Groups", "Community", "Caregiver Resources"],
    services: [
      "Online support groups",
      "Patient forums",
      "Caregiver resources",
      "Educational webinars",
    ],
  },
  {
    id: 3,
    name: "Amyloidosis Foundation",
    acronym: "AF",
    emoji: "💛",
    description:
      "Dedicated to finding a cure for amyloidosis through funding research, raising awareness, and providing resources to patients and medical professionals.",
    website: "https://amyloidosis.org",
    category: "Advocacy",
    color: "#f6a623",
    tags: ["Advocacy", "Research Funding", "Awareness"],
    services: [
      "Research grants",
      "Patient education",
      "Physician resources",
      "Financial assistance",
    ],
  },
  {
    id: 4,
    name: "National Organization for Rare Disorders",
    acronym: "NORD",
    emoji: "🌐",
    description:
      "A patient advocacy organization committed to the identification, treatment, and cure of rare diseases through education, advocacy, research, and patient services.",
    website: "https://rarediseases.org",
    category: "Advocacy",
    color: "#4facfe",
    tags: ["Rare Disease", "Advocacy", "Financial Assistance"],
    services: [
      "Patient assistance programs",
      "Disease information",
      "Rare disease advocacy",
      "Medication assistance",
    ],
  },
  {
    id: 5,
    name: "Leukemia & Lymphoma Society",
    acronym: "LLS",
    emoji: "🎗️",
    description:
      "Funds blood cancer research and provides free education and support services for AL amyloidosis patients and their families.",
    website: "https://lls.org",
    category: "Financial Aid",
    color: "#43e97b",
    tags: ["AL Amyloidosis", "Financial Aid", "Support"],
    services: [
      "Co-pay assistance",
      "Travel reimbursement",
      "Patient navigation",
      "Peer support",
    ],
  },
  {
    id: 6,
    name: "Patient Advocate Foundation",
    acronym: "PAF",
    emoji: "🛡️",
    description:
      "Provides case management services and financial aid to Americans with chronic, life-threatening, and debilitating illnesses including amyloidosis.",
    website: "https://patientadvocate.org",
    category: "Financial Aid",
    color: "#fa709a",
    tags: ["Case Management", "Financial Aid", "Insurance Help"],
    services: [
      "Case management",
      "Financial aid fund",
      "Insurance negotiation",
      "Co-pay relief",
    ],
  },
  {
  id: 13,
  name: "Cleveland Clinic Amyloidosis Center",
  acronym: "CC",
  emoji: "🏥",
  description:
    "One of the nation's leading amyloidosis programs offering comprehensive diagnosis, treatment, and research for all types of amyloidosis including AL, ATTR, and AA amyloidosis.",
  website: "https://my.clevelandclinic.org/departments/heart/depts/amyloidosis",
  category: "Medical Center",
  color: "#0a6fbc",
  tags: ["ATTR-CM", "AL Amyloidosis", "Cardiac Amyloidosis", "Research"],
  services: [
    "Multidisciplinary amyloidosis clinic",
    "Advanced cardiac imaging",
    "Clinical trials",
    "Second opinions",
  ],
},
  {
    id: 7,
    name: "Mayo Clinic Amyloidosis Program",
    acronym: "Mayo",
    emoji: "🏥",
    description:
      "One of the world's leading centers for amyloidosis diagnosis and treatment, offering comprehensive care, cutting-edge research, and patient education resources.",
    website: "https://mayoclinic.org/diseases-conditions/amyloidosis",
    category: "Medical Center",
    color: "#0B2545",
    tags: ["Diagnosis", "Treatment", "Research", "Education"],
    services: [
      "Specialized diagnosis",
      "Multidisciplinary care",
      "Clinical trials",
      "Second opinions",
    ],
  },
  {
    id: 8,
    name: "Boston University Amyloid Treatment & Research Program",
    acronym: "BU ATRP",
    emoji: "🎓",
    description:
      "A world-renowned center for amyloidosis research and treatment, home to leading experts in AA, AL, and hereditary ATTR amyloidosis.",
    website: "https://bumc.bu.edu/amyloid",
    category: "Medical Center",
    color: "#cc2936",
    tags: ["ATTR", "AL Amyloidosis", "Research", "Hereditary"],
    services: [
      "Specialized clinics",
      "Genetic counseling",
      "Research programs",
      "Patient education",
    ],
  },
  {
    id: 9,
    name: "HealthWell Foundation",
    acronym: "HWF",
    emoji: "💊",
    description:
      "Provides financial assistance to underinsured patients with amyloidosis to help cover medication costs, insurance premiums, and other treatment-related expenses.",
    website: "https://healthwellfoundation.org",
    category: "Financial Aid",
    color: "#11998e",
    tags: ["Medication Assistance", "Insurance Help", "Financial Aid"],
    services: [
      "Premium assistance",
      "Medication co-pay help",
      "Treatment cost support",
    ],
  },
  {
    id: 10,
    name: "National Institutes of Health — Rare Disease Network",
    acronym: "NIH",
    emoji: "🧬",
    description:
      "Federal research institution funding amyloidosis studies and maintaining the Genetic and Rare Diseases Information Center with comprehensive disease resources.",
    website: "https://rarediseases.info.nih.gov",
    category: "Research",
    color: "#1a5fa8",
    tags: ["Research Funding", "Disease Information", "Government"],
    services: [
      "Research grants",
      "Disease information center",
      "Clinical trial listings",
      "Expert referrals",
    ],
  },
  {
    id: 11,
    name: "Tafamidis Patient Support Program",
    acronym: "Vyndaqel",
    emoji: "💙",
    description:
      "Pfizer's patient assistance program for tafamidis (Vyndaqel/Vyndamax), offering financial support and resources for ATTR cardiomyopathy patients.",
    website: "https://vyndaqel.com/support",
    category: "Financial Aid",
    color: "#0073b6",
    tags: ["ATTR-CM", "Tafamidis", "Medication Assistance"],
    services: [
      "Co-pay assistance",
      "Free medication program",
      "Patient support hotline",
    ],
  },
  {
    id: 12,
    name: "Alnylam Patient Services",
    acronym: "Alnylam",
    emoji: "🧪",
    description:
      "Comprehensive support program for patients prescribed patisiran (Onpattro) or vutrisiran (Amvuttra) for hereditary ATTR amyloidosis with polyneuropathy.",
    website: "https://alnylam.com/patients",
    category: "Financial Aid",
    color: "#e05c3a",
    tags: ["hATTR", "Patisiran", "Vutrisiran", "Medication Assistance"],
    services: [
      "Insurance navigation",
      "Co-pay assistance",
      "Free drug program",
      "Nurse support",
    ],
  },
  
];

const CATEGORIES = ["All", "Research", "Support", "Advocacy", "Financial Aid", "Medical Center"];

const categoryIcons = {
  Research: <ScienceIcon />,
  Support: <GroupsIcon />,
  Advocacy: <VolunteerActivismIcon />,
  "Financial Aid": <AttachMoneyIcon />,
  "Medical Center": <BusinessIcon />,
  All: <PublicIcon />,
};

const SupportOrganizations = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = ORGANIZATIONS.filter((org) => {
    const matchesSearch =
      search === "" ||
      org.name.toLowerCase().includes(search.toLowerCase()) ||
      org.description.toLowerCase().includes(search.toLowerCase()) ||
      org.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory =
      activeCategory === "All" || org.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 5, textAlign: "center" }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Support Organizations
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Patient advocacy groups, research organizations, and financial assistance programs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {ORGANIZATIONS.length} organizations to support your amyloidosis journey
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ maxWidth: 560, mx: "auto", mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search organizations, services, or amyloid type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#1A5EA8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              "&:hover fieldset": { borderColor: "#1A5EA8" },
              "&.Mui-focused fieldset": { borderColor: "#1A5EA8" },
            },
          }}
        />
      </Box>

      {/* Category Filter */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center", mb: 5 }}>
        {CATEGORIES.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            icon={categoryIcons[cat]}
            onClick={() => setActiveCategory(cat)}
            variant={activeCategory === cat ? "filled" : "outlined"}
            sx={{
              px: 1,
              fontWeight: activeCategory === cat ? 700 : 400,
              background: activeCategory === cat
                ? "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)"
                : "transparent",
              color: activeCategory === cat ? "white" : "#1A5EA8",
              borderColor: "#1A5EA8",
              "& .MuiChip-icon": {
                color: activeCategory === cat ? "white" : "#1A5EA8",
              },
              "&:hover": {
                background: activeCategory === cat
                  ? "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)"
                  : "rgba(102,126,234,0.08)",
              },
            }}
          />
        ))}
      </Box>

      {/* Results count */}
      {search || activeCategory !== "All" ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
          Showing {filtered.length} of {ORGANIZATIONS.length} organizations
        </Typography>
      ) : null}

      {/* Organization Cards */}
      <Grid container spacing={4}>
        {filtered.map((org) => (
          <Grid item xs={12} md={6} lg={4} key={org.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: "2px solid",
                borderColor: "grey.200",
                boxShadow: 2,
                borderRadius: 3,
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: 8,
                  borderColor: org.color,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Header row */}
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 56, height: 56, borderRadius: 2, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: `linear-gradient(135deg, ${org.color}22 0%, ${org.color}44 100%)`,
                      fontSize: "28px",
                    }}
                  >
                    {org.emoji}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3, mb: 0.5 }}>
                      {org.name}
                    </Typography>
                    <Chip
                      label={org.category}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        bgcolor: `${org.color}18`,
                        color: org.color,
                      }}
                    />
                  </Box>
                </Box>

                {/* Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {org.description}
                </Typography>

                {/* Services */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Services
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                    {org.services.map((service, i) => (
                      <Typography component="li" key={i} variant="body2"
                        color="text.secondary" sx={{ mb: 0.25, fontSize: "0.8rem" }}>
                        {service}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* Tags */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {org.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        borderColor: "grey.300",
                        color: "text.secondary",
                      }}
                    />
                  ))}
                </Box>
              </CardContent>

              <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    background: `linear-gradient(135deg, ${org.color} 0%, ${org.color}cc 100%)`,
                    fontWeight: 600,
                    borderRadius: 2,
                    py: 1,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${org.color}dd 0%, ${org.color}aa 100%)`,
                    },
                  }}
                >
                  Visit Website
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No results */}
      {filtered.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No organizations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try a different search term or category
          </Typography>
        </Box>
      )}

      {/* Footer note */}
      <Box sx={{ mt: 6, pt: 3, borderTop: "1px solid", borderColor: "divider", textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          Know of an organization we should add? Contact us at{" "}
          <Box component="span" sx={{ color: "#1A5EA8", fontWeight: 600 }}>
            support@nephrotrax.com or(sandeep.sinha@verainsight.com)
          </Box>
        </Typography>
      </Box>
    </Container>
  );
};

export default SupportOrganizations;